import { IUIUrval } from '../../../../../../Types/UrvalTypes';
import { IEkonomiskAnalysFilledDates } from '../Components/AnalysSettingsHandler/AnalysSettingsHandler';
import {
  AnalysLevelEnum,
  AnalysTypeEnum,
  MISSING_VALUE_IN_KODDEL,
  VERKSAMHETSOMRADE_SAKNAS,
} from '../Const/EkonomiskAnalysConsts';
import { ISelection } from '../Queries/EkonomiskAnalysListQuery/EkonomiskAnalysListQuery';
import { ISumData } from '../Queries/EkonomiskAnalysSettingsQuery/EkonomiskAnalysSettingsQuery';
import {
  getNextSelectedSubniva,
  getKontodelString,
  IUrvalUsedForDrilling,
  getUrvalAttributeAndConditionsValues,
  createSearchStringForDrilling,
} from './detaljanalysUtils';
import { getAnalysByIDKod } from './userInfoUtils';

const getNewAnalysLevel = (
  sums: ISumData[],
  rubrikIndex: number,
  jumpToLastLevel: boolean,
  newSelectedSubniva: string
): AnalysLevelEnum | undefined => {
  //Hoppa direkt till sista nivån om verksamhetsområde saknas
  if (jumpToLastLevel) {
    return AnalysLevelEnum.LAST;
  }
  //Vi behöver bara ta reda på om vi är på mitten eller sista nivån med specialhantering om vi klickar på verksamhet i fallet att man vill se sista näst sista nivån och den sista
  if (rubrikIndex !== -1) {
    // is sumIndex in the third to last level (verksamhet) in the array hence edge case
    const isEdgeCaseForVerksamhet = rubrikIndex === sums.length - 3;

    const isLastSumLevel = sums[sums.length - 1].Namn === newSelectedSubniva;

    const isLastLevel = isEdgeCaseForVerksamhet || isLastSumLevel;
    if (isLastLevel) {
      return AnalysLevelEnum.LAST;
    } else {
      return AnalysLevelEnum.MIDDLE;
    }
  }
  return undefined;
};

const getSumsLopNr = (
  sums: ISumData[],
  analysLevel: AnalysLevelEnum,
  rubrikIndex: number,
  jumpToLastAnalysLevel: boolean
): number[] => {
  if (jumpToLastAnalysLevel && jumpToLastAnalysLevel === true) {
    return [2, 3, 4]; //Hårdkodat för att hoppa till verksamhet-nivån när verksamhetsområde saknas
  }

  if (rubrikIndex !== -1) {
    if (analysLevel === AnalysLevelEnum.LAST) {
      //Ta med två nivåer ner utifrån summeringnivån
      return sums
        .slice(rubrikIndex, rubrikIndex + 3)
        .map(summering => summering.LOPNR ?? -1);
    } else {
      //slice inkluderar inte 'end' parametern => +2 för att få med en nivå ner
      const result = sums
        .slice(rubrikIndex, rubrikIndex + 2)
        .map(summering => summering.LOPNR ?? -1);
      return result;
    }
  }

  return [];
};

export const redirectToNewDetaljAnalys = (
  analysId: string,
  analysLevel: AnalysLevelEnum,
  clickedSublvl: string | number,
  dates: IEkonomiskAnalysFilledDates,
  frontendUrl: string,
  jumpToLastAnalysLevel: boolean,
  rubrikNiva: string | undefined,
  selectedSubniva: string | undefined,
  selectedAnalysCodeprivilegeID: string,
  sums: ISumData[],
  urval: IUIUrval | null,
  drillToSameLevel: boolean
) => {
  const newSelectedSubniva = getNextSelectedSubniva(
    jumpToLastAnalysLevel,
    rubrikNiva,
    selectedSubniva
  );

  // Exit om den inte vet vart den ska borra
  if (newSelectedSubniva === undefined) return;

  const rubrikIndex = sums.findIndex(summering => {
    if (analysLevel === AnalysLevelEnum.UNSET && rubrikNiva) {
      return summering.Namn === rubrikNiva;
    } else {
      return summering.Namn === selectedSubniva;
    }
  });

  //Ska vi behöva hantera flera pages?
  const currentUrval = getUrvalAttributeAndConditionsValues(
    urval?.pages[0].conditionObjects
  );
  let newAnalysLevel: AnalysLevelEnum | undefined = undefined;

  if (drillToSameLevel) {
    newAnalysLevel = analysLevel;
  } else {
    newAnalysLevel = getNewAnalysLevel(
      sums,
      rubrikIndex,
      jumpToLastAnalysLevel,
      newSelectedSubniva
    );

    if (newAnalysLevel === undefined) return;
  }
  let selectedKoddel = '';

  selectedKoddel = getKontodelString(clickedSublvl.toString());

  const newSelectedUrval: IUrvalUsedForDrilling = {
    attributeDisplayName: newSelectedSubniva,
    conditionValue: selectedKoddel,
    missingValue: selectedKoddel.includes(MISSING_VALUE_IN_KODDEL),
  };

  currentUrval.push(newSelectedUrval);

  const sumLopNrArray = getSumsLopNr(
    sums,
    newAnalysLevel,
    rubrikIndex,
    jumpToLastAnalysLevel
  );

  const path = `${frontendUrl}/Rutin/EkonomiskAnalys`;

  const searchString = createSearchStringForDrilling(
    sumLopNrArray,
    dates.from.year,
    dates.from.month,
    dates.from.day,
    newAnalysLevel,
    analysId,
    selectedAnalysCodeprivilegeID,
    currentUrval
  );

  const fullPath = `${path}?${searchString.toString()}`;

  window.open(fullPath, '_blank');
};

export const borraIntaktEllerKostnad = (
  rubrikNiva: string | undefined,
  selectedSubniva: string | undefined,
  clickedSublvl: string | number,
  selectedAnalysCodeprivilegeID: string,
  urval: IUIUrval | null,
  analysLevel: AnalysLevelEnum,
  availableReports: ISelection[],
  dates: IEkonomiskAnalysFilledDates,
  idKod: string,
  frontendUrl: string,
  sums: ISumData[],
  drillToSameLevel: boolean,
  analysType: AnalysTypeEnum
) => {
  //TODO: Här kommer vi istället att vilka kolla på en markering om den klickade baskontot har bokförts utan verksamhetskod
  // Se https://eniacdata.atlassian.net/browse/EK-7720
  const jumpToLastAnalysLevel =
    (analysType === AnalysTypeEnum.INTAKTSUPPFOLJNING ||
      analysType === AnalysTypeEnum.RESULTATUPPFOLJNING) &&
    clickedSublvl === VERKSAMHETSOMRADE_SAKNAS;

  const newUppfoljningAnalys = getAnalysByIDKod(idKod, availableReports);

  if (newUppfoljningAnalys == null || newUppfoljningAnalys.id === undefined) {
    //TODO: bättre felhantering
    console.warn(`Hittar inte rapport med idKod ${idKod}`);
    return;
  }

  redirectToNewDetaljAnalys(
    newUppfoljningAnalys.id,
    analysLevel,
    clickedSublvl,
    dates,
    frontendUrl,
    jumpToLastAnalysLevel,
    rubrikNiva,
    selectedSubniva,
    selectedAnalysCodeprivilegeID,
    sums,
    urval,
    drillToSameLevel
  );
};
